import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Conversation, ItemDetail, Member, SpaceOut } from "../api/types.gen.ts";
import { type Key, t } from "../i18n/index.ts";
import type { Action } from "../lib/actions.ts";
import { attachTarget } from "../lib/attach.ts";
import { isBookmarked, setBookmark } from "../lib/bookmarks.ts";
import { copyText } from "../lib/clipboard.ts";
import { useConversation } from "../lib/conversation.ts";
import { patchItem, useEntities, useItem } from "../lib/data.ts";
import { useDropTarget } from "../lib/fileDrop.ts";
import { isZipItem } from "../lib/folders.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { ignore } from "../lib/ignore.ts";
import { EMAIL_MIME, filesOf, ownDocument } from "../lib/itemFiles.ts";
import { sideShown } from "../lib/itemSide.ts";
import { elsewhere } from "../lib/keepInView.ts";
import { outsideOf, spaceApi, spaceUrl } from "../lib/paths.ts";
import { usePeople } from "../lib/people.ts";
import { outsideRoom } from "../lib/presence.ts";
import { type Resource, useAction } from "../lib/resource.ts";
import { atLeast, canEdit } from "../lib/roles.ts";
import { navigate, tabHref, tabParam } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { titleOf } from "../lib/titles.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { itemLink } from "../lib/visibility.ts";
import { BookmarkButton } from "../ui/BookmarkButton.tsx";
import { ConfirmDialog, Dialog } from "../ui/Dialog.tsx";
import {
  ArchivedIcon,
  BookmarkedIcon,
  BookmarkIcon,
  EraseHistoryIcon,
  FolderIcon,
  LinkIcon,
  RenameIcon,
  RequestReviewIcon,
  RestoreIcon,
  ReviewIcon,
  TrashIcon,
  ZipIcon,
} from "../ui/icons.tsx";
import type { MenuEntry } from "../ui/Menu.tsx";
import { Async, Badge } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { PresenceStack } from "../ui/PresenceStack.tsx";
import { buttonClass } from "../ui/styles.ts";
import { TabNav } from "../ui/TabNav.tsx";
import { TriageIcon } from "../ui/TriageIcon.tsx";
import { ConversationLabel, ConversationTab } from "./comments/conversations.tsx";
import { ItemPlace, UnpackDialog } from "./folders/itemPlace.tsx";
import { MoveDialog } from "./folders/move.tsx";
import { CommitList } from "./history/commitList.tsx";
import { Attachments } from "./item/attachments.tsx";
import { ReferencedBy } from "./item/backrefs.tsx";
import { ItemFields } from "./item/form.tsx";
import { notesSlot, reviewAsked } from "./item/notesSlot.ts";
import { DocumentTabEnd, DocumentTabName, FileBeta, Files, Issues, ItemRules, Reviews } from "./item/panels.tsx";
import { ReviewFindings, ReviewPane } from "./item/ReviewPane.tsx";
import { RequestButton, Requests } from "./item/requests.tsx";
import { ReviewForm } from "./item/reviewForm.tsx";
import { OutsideBanner, ShareButton, SharedIcon, VisibilityMark } from "./item/visibility.tsx";
import { EntityLabel } from "./items/entityLabel.tsx";
import { KindIcon } from "./items/icons.tsx";

const TABS = ["file", "details", "issues", "reviews", "referenced", "conversation", "history"] as const;
/** Tabs of what's beside the page when it's wide (its issues, its reviews): where it's narrow, one column. */
const NARROW: readonly Tab[] = ["issues", "reviews"];
type Tab = (typeof TABS)[number];

/**
 * The item's tabs: its file(s) first when it is or has any (to see them right away), then details, conversation and
 * history. From outside its space: no history, and its conversation where it shows it.
 */
function tabsOf(item: ItemDetail, space: SpaceOut): readonly [Tab, ...Tab[]] {
  const out = outsideOf(space);
  const tabs = TABS.filter(
    (x) =>
      (x !== "file" || filesOf(item).length > 0) &&
      // What refers to it, when anything does.
      (x !== "referenced" || item.referenced_by.length > 0) &&
      (!out || (x !== "history" && (x !== "conversation" || out.shows.conversation))),
  );
  // Details are always there.
  return tabs as [Tab, ...Tab[]];
}

/** What the item's page and its parts work with. */
interface Page {
  space: SpaceOut;
  r: Resource<ItemDetail>;
  item: ItemDetail;
  /** The space's members (none from outside it). */
  members: readonly Member[] | undefined;
  conv: Resource<Conversation>;
}

/** The rest, out of the way in "More" (so the buttons stay in one row beside the title): its link, who sees it, the
 *  triage area, archiving, deleting. */
/** The item's "⋯" entries: what's done to it beyond the header's buttons (each in its dialog, or straight away). */
function useMoreEntries({
  space,
  r,
  item,
  onConfirm,
  onRename,
  onMove,
  onUnpack,
  onEraseHistory,
}: Page & {
  onConfirm: () => void;
  onRename: () => void;
  /** "Move to…" a folder. */
  onMove: () => void;
  /** A zip's "Unpack here…". */
  onUnpack: () => void;
  /** Admins: erasing its history (asked first). */
  onEraseHistory: () => void;
}): MenuEntry[] {
  // One change at a time, each on the version the last one left.
  const [change, changing] = useAction(async (body: Record<string, unknown>, done: Key) => {
    r.set(await patchItem(space, item, body));
    toast(t(done));
  });
  const act = (body: Record<string, unknown>, done: Key) => (changing ? undefined : change(body, done));
  const copyLink = () =>
    copyText(itemLink(item.id)).then(
      (ok) => toast(t(ok ? "visibility.linkCopied" : "visibility.linkNotCopied")),
      ignore,
    );
  return [
    {
      label: t("item.rename"),
      icon: <RenameIcon />,
      onSelect: onRename,
    },
    { label: t("folders.moveTo"), icon: <FolderIcon />, onSelect: onMove },
    ...(isZipItem(item) ? [{ label: t("folders.unpackHere"), icon: <ZipIcon />, onSelect: onUnpack }] : []),
    {
      label: t("visibility.copyLink"),
      icon: <LinkIcon />,
      onSelect: () => copyLink(),
    },
    // In the triage area: take it out (without filing it anywhere). Out of it: back in, for someone to look at again.
    {
      label: item.triage ? t("triage.takeOut") : t("triage.putBack"),
      // Our own picture: on a narrow screen only.
      icon: <TriageIcon class="sm:hidden" />,
      onSelect: () => act({ triage: !item.triage }, item.triage ? "triage.takenOut" : "triage.backIn"),
    },
    {
      label: t(item.archived ? "item.unarchive" : "item.archive"),
      icon: <ArchivedIcon />,
      onSelect: () => act({ archived: !item.archived }, item.archived ? "item.unarchived" : "item.archived"),
    },
    // Apart, what can't be had back as easily. Admins: its past erased for good (what it came as with it).
    ...(atLeast(space.role, "admin")
      ? [
          {
            label: t("item.eraseHistory"),
            icon: <EraseHistoryIcon />,
            onSelect: onEraseHistory,
            danger: true,
            separated: true,
          },
        ]
      : []),
    {
      label: t("common.delete"),
      icon: <TrashIcon />,
      onSelect: onConfirm,
      danger: true,
      ...(atLeast(space.role, "admin") ? {} : { separated: true }),
    },
  ];
}

function Header(p: Page & { onReview: () => void; sharePanel: string }) {
  const { space, r, item, members } = p;
  const [confirm, setConfirm] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [moving, setMoving] = useState(false);
  const [unpacking, setUnpacking] = useState(false);
  // The rename field's, so what it came as (under the title) goes into it.
  const titleField = useId();
  const restoreName = (original: string) => {
    const field = document.getElementById(titleField);
    if (field instanceof HTMLInputElement) {
      field.value = original;
      field.form?.requestSubmit();
    }
  };
  const [rename] = useAction(async (title: string) => {
    r.set(await patchItem(space, item, { title }));
    toast(t("item.renamed"));
  });
  const [eraseHistory] = useAction(async () => {
    await api.del(spaceApi(space, `/items/${item.id}/history`));
    toast(t("item.historyErased"));
    r.reload();
  });
  const [remove] = useAction(async () => {
    await api.del(spaceApi(space, `/items/${item.id}`));
    toast(t("item.deleted"));
    navigate(spaceUrl(space, "/items"));
  });
  const out = outsideOf(space);
  const outside = out !== undefined;
  // Who's on it: in its room, or an invited guest's (`shared:`); by a public link, nobody's shown.
  const room = out ? (out.api.startsWith("/shared/") ? outsideRoom(out.api) : null) : `item:${item.id}`;
  const requesting = useRef<(() => void) | null>(null);
  const itemTarget = { kind: "item", item: item.id } as const;
  const bookmarked = isBookmarked(space.id, itemTarget);
  const editable = !outside && canEdit(space);
  const more = useMoreEntries({
    ...p,
    onConfirm: () => setConfirm(true),
    onRename: () => setRenaming(true),
    onMove: () => setMoving(true),
    onUnpack: () => setUnpacking(true),
    onEraseHistory: () => setErasing(true),
  });
  const bookmark: Action = {
    key: "bookmark",
    label: t(bookmarked ? "bookmarks.remove" : "bookmarks.add"),
    icon: bookmarked ? <BookmarkedIcon class="text-blue-600" /> : <BookmarkIcon />,
    symbol: bookmarked ? "bookmark.fill" : "bookmark",
    drop: 30,
    onSelect: () => {
      setBookmark(space.id, itemTarget, !bookmarked).catch(ignore);
    },
    node: <BookmarkButton space={space} target={itemTarget} size="md" variant="secondary" />,
  };
  // Beside the title, as many as fit (the review first), the rest in "⋯": each one's own button still here (hidden
  // when it's in "⋯"), its dialog or panel opening from there.
  const buttons: Action[] = editable
    ? [
        {
          key: "request",
          label: t("requests.button"),
          icon: <RequestReviewIcon />,
          symbol: "person.badge.clock",
          drop: 40,
          onSelect: () => requesting.current?.(),
          node: <RequestButton space={space} item={item} members={members} onDone={r.reload} opener={requesting} />,
        },
        {
          key: "review",
          label: t("review.button"),
          icon: <ReviewIcon />,
          symbol: "checkmark.seal",
          primary: true,
          fab: true,
          drop: 10,
          onSelect: p.onReview,
        },
        {
          key: "share",
          label: t("visibility.share"),
          icon: <SharedIcon item={item} />,
          symbol: "person.2",
          drop: 20,
          onSelect: () => document.getElementById(p.sharePanel)?.showPopover(),
          node: (
            <ShareButton
              space={space}
              item={item}
              members={members}
              onDone={r.set}
              class={buttonClass("secondary")}
              panel={p.sharePanel}
            />
          ),
        },
        bookmark,
        ...more.map(
          (e): Action => ({
            key: `more:${e.key ?? e.label}`,
            label: e.label,
            icon: e.icon,
            overflow: true,
            onSelect: e.onSelect,
            ...(e.danger ? { danger: true } : {}),
            ...(e.separated ? { separated: true } : {}),
          }),
        ),
      ]
    : [bookmark];
  return (
    <>
      <PageHeader
        title={titleOf(item)}
        icon={<KindIcon item={item} />}
        id={outside ? undefined : item.id}
        above={
          <>
            {outside ? <p class="mb-1 text-xs text-zinc-500">{t("public.sharedBy", { space: space.name })}</p> : null}
            {/* Where it is among the space's folders (and whether it's shared as its folder is). */}
            {outside ? null : <ItemPlace space={space} item={item} drift={canEdit(space)} onChange={r.reload} />}
          </>
        }
        marks={
          <>
            {outside ? null : <VisibilityMark item={item} members={members} size={18} />}
            {room ? <PresenceStack room={room} /> : null}
          </>
        }
        rename={
          editable
            ? {
                editing: renaming,
                onEdit: setRenaming,
                onRename: (title) => rename(title),
                value: item.title ?? "",
                label: t("item.rename"),
                field: titleField,
                original: item.original_title,
              }
            : undefined
        }
        details={
          <p class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-500">
            {/* What it came as, when renamed since (the label says so on hover); while renaming, a click goes back to it. */}
            {item.original_title && renaming ? (
              <button
                type="button"
                class="flex min-w-0 items-center gap-1 rounded hover:text-zinc-800"
                title={t("item.restoreName")}
                // The field keeps its focus (leaving it would keep what's typed).
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => restoreName(item.original_title ?? "")}
              >
                <RestoreIcon size={13} class="shrink-0" />
                <span class="truncate">{item.original_title}</span>
              </button>
            ) : item.original_title ? (
              <span class="min-w-0 truncate" title={t("item.originalName", { name: item.original_title })}>
                {item.original_title}
              </span>
            ) : null}
            <EntityLabel entity={item.schema} />
            {item.triage ? <Badge tone="amber">{t("item.inTriage")}</Badge> : null}
            {item.archived ? <Badge tone="gray">{t("item.archivedBadge")}</Badge> : null}
            {item.comment ? (
              <Badge tone="gray" title={t("item.reportHint")}>
                {t("attached.report")}
              </Badge>
            ) : null}
            <span>{t("item.createdAt", { at: formatInstant(item.created_at) })}</span>
            <span title={formatInstant(item.updated_at)}>
              {t("item.updatedAt", { at: formatRelative(item.updated_at) })}
            </span>
          </p>
        }
        buttons={buttons}
        more={t("item.more")}
      />
      {editable ? (
        <>
          <ConfirmDialog
            open={erasing}
            onClose={() => setErasing(false)}
            title={t("item.eraseHistoryTitle")}
            message={t("item.eraseHistoryMessage")}
            confirmLabel={t("item.eraseHistoryConfirm")}
            onConfirm={() => eraseHistory()}
          />
          <MoveDialog
            space={space}
            open={moving}
            title={t("folders.moveTitle", { name: item.title ?? "" })}
            moving={{ kind: "items", ids: [item.id], items: [item], triage: item.triage }}
            onClose={() => setMoving(false)}
            onDone={r.reload}
          />
          <UnpackDialog space={space} item={item} open={unpacking} onClose={() => setUnpacking(false)} />
          {/* It goes to the Trash (where admins can delete it permanently). */}
          <ConfirmDialog
            open={confirm}
            onClose={() => setConfirm(false)}
            title={t("item.deleteTitle")}
            message={t("item.deleteMessage")}
            confirmLabel={t("common.delete")}
            onConfirm={() => remove()}
          />
        </>
      ) : null}
    </>
  );
}

/** The File tab's name: "Email" for an email (its attachments under it), else "File" or "3 files". */
function fileTab(item: ItemDetail, files: number): string {
  if (item.file?.mime === EMAIL_MIME) {
    return t("item.tab.email");
  }
  return files > 1 ? t("item.tab.files", { count: files }) : t("item.tab.file");
}

/** A tab's name, with how many there are where it lists something. */
function tabLabel(x: Tab, { item, conv }: Page): ComponentChildren {
  const count = (n: number) => <span class="text-zinc-400 tabular-nums">{n}</span>;
  switch (x) {
    case "file":
      return fileTab(item, filesOf(item).length);
    case "referenced":
      return (
        <>
          {t("item.referencedBy")} {count(item.referenced_by.length)}
        </>
      );
    case "issues":
    case "reviews":
      return (
        <>
          {t(`item.tab.${x}`)} {count((x === "issues" ? item.issues : item.reviews).length)}
        </>
      );
    case "conversation":
      return <ConversationLabel conv={conv} />;
    default:
      return t(`item.tab.${x}`);
  }
}

/**
 * The item's tabs. With its own document filling the File tab, that tab is named after it ("Word document", Beta
 * beside) and, while it's shown, its size, download and tools are at the bar's end: no row of their own over it.
 */
function Tabs({ page, tabs, tab }: { page: Page; tabs: readonly [Tab, ...Tab[]]; tab: Tab }) {
  const { space, item } = page;
  const base = outsideOf(space)?.home ?? spaceUrl(space, `/items/${item.id}`);
  const doc = ownDocument(item);
  return (
    <TabNav
      label={t("item.tabs")}
      tabs={tabs.map((x) => ({
        href: tabHref(base, x === tabs[0] ? null : x),
        label: x === "file" && doc ? <DocumentTabName f={doc} /> : tabLabel(x, page),
        ...(x === "file" && doc ? { after: <FileBeta space={space} f={doc} /> } : {}),
        current: x === tab,
        narrow: NARROW.includes(x),
      }))}
      end={tab === "file" && doc ? <DocumentTabEnd space={space} f={doc} /> : null}
    />
  );
}

/** Its issues; with `extras`, its review requests too (in its space). */
function IssuesSide({ extras = true, ...p }: Page & { extras?: boolean }) {
  return (
    <>
      <Issues
        space={p.space}
        item={p.item}
        members={p.members}
        onChange={p.r.reload}
        conv={p.conv}
        before={
          notesSlot.value?.item === p.item.id ? (
            <ReviewFindings space={p.space} item={p.item.id} rules={p.item.rules} {...notesSlot.value.pane} />
          ) : null
        }
      />
      {extras && !outsideOf(p.space) ? <Requests space={p.space} item={p.item} onChange={p.r.reload} /> : null}
    </>
  );
}

/** Its reviews; with `extras`, the rules it's reviewed by too (in its space). */
function ReviewsSide({ extras = true, ...p }: Page & { extras?: boolean }) {
  return (
    <>
      <Reviews space={p.space} item={p.item} members={p.members} conv={p.conv} />
      {extras && !outsideOf(p.space) ? <ItemRules space={p.space} item={p.item} members={p.members} /> : null}
    </>
  );
}

/** Beside the page's main column when it's wide (narrow, these are tabs of their own: where whoever was in them goes). */
function Aside(p: Page & { extras?: boolean; hidden?: boolean }) {
  // Beside its own document, sheet or PDF (filling the File tab): as tall as the file and no taller, scrolling by itself (its notes
  // can be many); the file sets the row's height. Docked: out to the window's right and bottom edges (over the page's
  // padding) and up to the tabs (over their margin); shown only (hidden, nothing peeks out). Its content (a border on
  // its left) laid out the same either way: folding, it's clipped as it goes, not reflowed.
  const own = p.extras === false && ownDocument(p.item) !== undefined;
  const docked = own && !p.hidden;
  // Docking eased as the panel is hidden and shown, not as the page opens (its notes arriving dock it).
  const [eased, setEased] = useState(false);
  useEffect(() => {
    if (!own) {
      return;
    }
    const f = requestAnimationFrame(() => setEased(true));
    return () => cancelAnimationFrame(f);
  }, [own]);
  return (
    // Hidden (File tab, SideToggle): its column closes to nothing; its content keeps its width as it goes (clipped,
    // not squeezed), and can't be reached. Its painting contained then too: a card positioned against something
    // further out escaped the clipping, and Safari widened the page by it.
    <aside
      class={`hidden overflow-hidden @3xl/main:block ${eased ? "transition-[margin] duration-300 ease-out motion-reduce:transition-none" : ""} ${own ? "relative" : ""} ${docked ? "@3xl/main:-mt-6 @3xl/main:-mr-8 @3xl/main:-mb-8" : ""} ${p.hidden ? "[contain:paint]" : ""}`}
      inert={p.hidden ? true : undefined}
    >
      <div
        class={`${own ? "@3xl/main:w-[24rem] @3xl/main:border-l @3xl/main:border-zinc-200 @3xl/main:px-6 @3xl/main:py-6" : "@3xl/main:w-[22rem]"} ${own ? "@3xl/main:absolute @3xl/main:inset-y-0 @3xl/main:left-0 @3xl/main:overflow-y-auto @3xl/main:overscroll-contain" : ""}`}
      >
        {/* Its document's comments in place, when it's shown (notesSlot.ts); its findings' with its issues. */}
        {notesSlot.value?.item === p.item.id ? (
          <ReviewPane space={p.space} item={p.item.id} {...notesSlot.value.pane} />
        ) : null}
        <div {...elsewhere("tab=issues")}>
          <IssuesSide {...p} />
        </div>
        <div {...elsewhere("tab=reviews")}>
          <ReviewsSide {...p} />
        </div>
      </div>
    </aside>
  );
}

function Details(p: Page) {
  const { space, r, item } = p;
  return (
    <div class="grid grid-cols-1 gap-8 @3xl/main:grid-cols-[minmax(0,1fr)_22rem]">
      <div>
        <ItemFields space={space} r={r} item={item} members={p.members} />
        <Attachments space={space} item={item} onChange={(next) => (next ? r.set(next) : r.reload())} />
      </div>
      <Aside {...p} />
    </div>
  );
}

/** What refers to the item (its tab). */
function Referenced({ space, item, members }: Page) {
  const entities = useEntities(space);
  return <ReferencedBy space={space} item={item} entities={entities.data ?? []} members={members} />;
}

/** The page's main tab (on a wide page, the first one when one of what's beside it is asked for). */
function Main({ main, ...p }: Page & { main: Tab }) {
  switch (main) {
    case "file":
      // Its file wants the room: only its issues and reviews beside it, and those can be hidden (SideToggle), their
      // column closing (and opening) smoothly.
      return (
        <div
          class={`grid grid-cols-1 transition-[grid-template-columns,column-gap] duration-300 ease-out motion-reduce:transition-none ${sideShown.value ? "gap-8 @3xl/main:grid-cols-[minmax(0,1fr)_22rem]" : "gap-y-8 @3xl/main:grid-cols-[minmax(0,1fr)_0rem] @3xl/main:gap-x-0"}`}
        >
          <Files space={p.space} item={p.item} />
          <Aside {...p} extras={false} hidden={!sideShown.value} />
        </div>
      );
    case "details":
      return <Details {...p} />;
    case "referenced":
      return <Referenced {...p} />;
    case "conversation":
      return <ConversationTab space={p.space} item={p.item.id} conv={p.conv} guests={p.item.invited} />;
    case "history":
      return <CommitList space={p.space} object={p.item.id} reviews={true} />;
    default:
      return null;
  }
}

/** An item's page: in its space, or (on a stand-in for it) seen from outside, read-only. */
export function ItemView({ space, r, item }: { space: SpaceOut; r: Resource<ItemDetail>; item: ItemDetail }) {
  const tabs = tabsOf(item, space);
  const tab = tabParam(tabs);
  const sharePanel = useId();
  const [reviewing, setReviewing] = useState(false);
  // Its document's Add finding with nothing selected: this review form.
  const asked = reviewAsked.value;
  const first = useRef(asked);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `asked` is the trigger: each time it's asked again.
  useEffect(() => {
    if (asked !== first.current) {
      setReviewing(true);
    }
  }, [asked]);
  // From outside the space, no one of it is listed (the last space's people aren't its).
  const people = usePeople();
  const page: Page = {
    space,
    r,
    item,
    members: outsideOf(space) ? undefined : people,
    conv: useConversation(space, { item: item.id }),
  };
  const beside = NARROW.includes(tab);
  // Files dropped anywhere on its page (whichever tab) are attached to it.
  useDropTarget(!outsideOf(space) && canEdit(space) ? attachTarget(space, item, r.reload) : null);
  return (
    <>
      <Header {...page} onReview={() => setReviewing(true)} sharePanel={sharePanel} />
      {outsideOf(space) ? null : <OutsideBanner item={item} panel={sharePanel} />}
      <Tabs page={page} tabs={tabs} tab={tab} />
      {beside ? (
        // Narrow: what's beside the page when wide, as its own tab; wide, it's beside the page's first tab (where
        // whoever was in it goes).
        <div class="@3xl/main:hidden" {...elsewhere("tab=")}>
          {tab === "issues" ? <IssuesSide {...page} /> : <ReviewsSide {...page} />}
        </div>
      ) : null}
      <div class={beside ? "hidden @3xl/main:block" : ""}>
        <Main {...page} main={beside ? tabs[0] : tab} />
      </div>
      <Dialog
        open={reviewing}
        onClose={() => setReviewing(false)}
        title={t("review.title", { title: item.title ?? "" })}
      >
        <ReviewForm
          space={space}
          item={item}
          onCancel={() => setReviewing(false)}
          onDone={() => {
            setReviewing(false);
            r.reload();
          }}
        />
      </Dialog>
    </>
  );
}

export function Item({ space, params }: PageProps) {
  const r = useItem(space, params["id"] ?? "");
  return (
    <Async r={r} whole="page">
      {(item) => <ItemView space={space} r={r} item={item} />}
    </Async>
  );
}
